import React, { useState } from "react";
import { Plane, Hotel as HotelIcon, Map, Car } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import Reveal from "./Reveal";
import TicketModal from "./TicketModal";
import TransportModal from "./TransportModal";

// 4 layanan utama — banner landscape.
// action: "paket" = scroll ke section paket, "hotel" = scroll ke section hotel, "ticket" = buka popup tiket, "transport" = buka popup transport, null = tanpa klik
const SERVICES = [
  {
    key: "transportation",
    img: "/services/transportation.png",
    Icon: Car,
    action: "transport",
    label: {
      id: "Transportasi",
      en: "Transportation",
      zh: "交通服务",
      ja: "送迎サービス",
      ko: "교통 서비스",
    },
    hint: {
      id: "Lihat harga sewa →",
      en: "See rental rates →",
      zh: "查看租车价格 →",
      ja: "レンタル料金を見る →",
      ko: "렌탈 요금 보기 →",
    },
  },
  {
    key: "tour-package",
    img: "/services/tour-package.png",
    Icon: Map,
    action: "paket",
    label: {
      id: "Paket Tour",
      en: "Tour Package",
      zh: "旅游配套",
      ja: "ツアーパッケージ",
      ko: "투어 패키지",
    },
    hint: {
      id: "Lihat semua paket →",
      en: "See all packages →",
      zh: "查看所有配套 →",
      ja: "すべてのパッケージ →",
      ko: "모든 패키지 보기 →",
    },
  },
  {
    key: "hotel",
    img: "/services/hotel.png",
    Icon: HotelIcon,
    action: "hotel",
    label: {
      id: "Hotel",
      en: "Hotel",
      zh: "酒店住宿",
      ja: "ホテル",
      ko: "호텔",
    },
    hint: {
      id: "Lihat pilihan hotel →",
      en: "See hotel options →",
      zh: "查看酒店选项 →",
      ja: "ホテルを見る →",
      ko: "호텔 보기 →",
    },
  },
  {
    key: "ticket",
    img: "/services/tiket-pesawat.png",
    Icon: Plane,
    action: "ticket",
    label: {
      id: "Tiket Ferry & Pesawat",
      en: "Ferry & Flight Tickets",
      zh: "渡轮与机票",
      ja: "フェリー＆航空券",
      ko: "페리 & 항공권",
    },
    hint: {
      id: "Pilih tiket →",
      en: "Choose your ticket →",
      zh: "选择票券 →",
      ja: "チケットを選ぶ →",
      ko: "티켓 선택 →",
    },
  },
];

const pick = (obj, lang) => obj[lang] || obj.id || "";

export const Layanan = () => {
  const { t, lang } = useLang();
  const [ticketOpen, setTicketOpen] = useState(false);
  const [transportOpen, setTransportOpen] = useState(false);

  const goToPaket = () => {
    const el = document.getElementById("paket");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const goToHotel = () => {
    const el = document.getElementById("hotel");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleAction = (action) => {
    if (action === "paket") goToPaket();
    if (action === "hotel") goToHotel();
    if (action === "ticket") setTicketOpen(true);
    if (action === "transport") setTransportOpen(true);
  };

  return (
    <section
      id="layanan"
      data-testid="layanan-section"
      className="bg-[#FBFAF6] py-24 md:py-32"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-12 lg:px-16">
        <div className="grid items-end gap-8 md:grid-cols-12 md:gap-12">
          <Reveal className="md:col-span-5">
            <span className="flex items-center gap-3 text-[11px] uppercase tracking-[0.28em] text-[#0E9A57]">
              <span className="h-px w-8 bg-[#0E9A57]" />
              {t.layanan.eyebrow}
            </span>
            <h2 className="mt-5 font-[Fraunces] text-4xl font-medium leading-[1.05] tracking-tight text-[#0C2230] sm:text-5xl">
              {t.layanan.title}
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-6 md:col-start-7">
            <p className="text-base leading-relaxed text-[#0C2230]/70 md:text-lg">
              {t.layanan.sub}
            </p>
          </Reveal>
        </div>

        {/* ===== 4 banner landscape ===== */}
        <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-2">
          {SERVICES.map((s, idx) => {
            const clickable = Boolean(s.action);
            const title = pick(s.label, lang);
            const hint = pick(s.hint, lang);

            return (
              <Reveal key={s.key} delay={idx * 0.06}>
                <article
                  data-testid={`layanan-card-${idx}`}
                  onClick={clickable ? () => handleAction(s.action) : undefined}
                  role={clickable ? "button" : undefined}
                  tabIndex={clickable ? 0 : undefined}
                  onKeyDown={
                    clickable
                      ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleAction(s.action);
                          }
                        }
                      : undefined
                  }
                  className={`group relative aspect-[16/9] overflow-hidden rounded-3xl ring-1 ring-[#0C2230]/10 shadow-[0_10px_30px_-14px_rgba(12,34,48,0.3)] transition-shadow duration-500 hover:shadow-[0_18px_44px_-16px_rgba(12,34,48,0.45)] ${
                    clickable ? "cursor-pointer" : ""
                  }`}
                >
                  <img
                    src={s.img}
                    alt={title}
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105"
                  />

                  {/* Hint muncul saat hover, hanya untuk kartu yang bisa diklik */}
                  {clickable && (
                    <>
                      <div className="absolute inset-0 bg-[#0C2230]/0 transition-colors duration-500 group-hover:bg-[#0C2230]/35" />
                      <div className="absolute inset-x-0 bottom-0 flex items-center gap-2 p-5 opacity-0 transition-all duration-500 group-hover:opacity-100">
                        <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#0E9A57] text-white shadow-md">
                          <s.Icon className="h-4 w-4" strokeWidth={1.8} />
                        </span>
                        <span className="rounded-full bg-white/95 px-4 py-1.5 text-xs font-semibold text-[#0C2230] shadow-md">
                          {hint}
                        </span>
                      </div>
                    </>
                  )}
                </article>
              </Reveal>
            );
          })}
        </div>
      </div>

      <TicketModal open={ticketOpen} onClose={() => setTicketOpen(false)} />
      <TransportModal
        open={transportOpen}
        onClose={() => setTransportOpen(false)}
        lang={lang}
      />
    </section>
  );
};

export default Layanan;
